import React, { useState, useRef, useEffect } from 'react';
import { getOrderId, isMobile } from '@/biz/tool';
import { shouldRequireCreatorAgreement } from '@/biz/creatorAgreement';
import toast from 'react-hot-toast';
import WebModal from '../Modal';
import BottomDrawer from '../BottomDrawer';
import type { StripeAddressElement } from '@stripe/stripe-js';
import { useDetailDataStore } from '@/hooks/useDetailDataStore';
import { useWebModalStore } from '@/hooks/useWebModalStore';
import axiosInstance from '@/apis/axiosInstance';
import { useTranslation } from 'react-i18next';
import getHoursDiffFromNow from '@/utils/getHoursDiffFromNow';
import { containsProhibitedPOBox, validateRFC } from './utils';
import TopStep from './TopStep';
import SelectProductContent from './SelectProductContent';
import ModalContent from './ModalContent';

interface Props {}

const ModalAddress: React.FC<Props> = ({}) => {
  const { t } = useTranslation();
  const { detailData, reQueryDetailData } = useDetailDataStore();
  const { isShowAddressModal, setShowAddressModal, isAgree } =
    useWebModalStore();
  const { setNowSuccessNode, setShowModalBonusNext, setShowModalNodeSuccess } =
    useWebModalStore();
  const [complete, setComplete] = useState(false);
  const [stripeComplete, setStripeComplete] = useState(false);
  const [poBoxInvalid, setPoBoxInvalid] = useState(false);
  const [address, setAddress] = useState<any>(null);
  const addressElementRef = useRef<StripeAddressElement | null>(null);
  const showRfcValidationRef = useRef<(() => void) | null>(null);
  const orderId = getOrderId();
  const [stepNodes, setStepNodes] = useState(1); // 0: 不需要节点，1: 填写信息  2：填写地址
  const { product, order } = detailData;
  const shouldShowCreatorAgreement = shouldRequireCreatorAgreement(
    detailData?.order?.appliedAt
  );
  const [creatorAgreementError, setCreatorAgreementError] = useState(false);
  const [addAddressToAccount, setAddAddressToAccount] = useState(false);
  const [skuListData, setSkuListData] = useState<any[]>(
    (order?.skuList?.length > 0 ? order?.skuList : product?.skuList) || []
  );
  useEffect(() => {
    const currentSkuList =
      (order?.skuList?.length > 0 ? order?.skuList : product?.skuList) || [];
    setSkuListData(currentSkuList);
    if (order?.skuList?.length > 0) {
      setStepNodes(2);
    } else if (product?.skuList?.length > 0) {
      setStepNodes(1);
    } else {
      setStepNodes(0);
    }
  }, [order?.skuList, product?.skuList]);

  const handleSubmit = async () => {
    if (!complete) {
      // Stripe AddressElement: getValue() validates and shows errors under each field
      await addressElementRef.current?.getValue();
      showRfcValidationRef.current?.();
      return;
    }
    if (shouldShowCreatorAgreement && !isAgree) {
      setCreatorAgreementError(true);
      return;
    }
    try {
      const addressText = `${(address as any)?.line1 || ''} ${(address as any)?.line2 || ''}`;
      if (containsProhibitedPOBox(addressText)) {
        toast.error(
          t('modal.address.no_po_box.error', {
            defaultValue:
              'Please provide a physical shipping address (no PO Boxes).'
          })
        );
        return;
      }
      await axiosInstance.post(`/order/${orderId}/address`, {
        addressInfo: address
      });
      // 勾选保存到账户时，静默新增地址，不影响主流程
      if (addAddressToAccount) {
        axiosInstance
          .post('/user/shipping-addresses', {
            addressInfo: address,
            isDefault: false
          })
          .catch(() => {});
      }
      reQueryDetailData({ orderId });
      setShowAddressModal(false);
      // 此处区分有无bonus，弹不同提示弹窗
      // 填写地址逾期时，要等提交成功后，才会清零奖金，所以特殊处理下，判断有没有逾期
      // 但是这个又有一种情况是，可以申诉，申诉成功后，可以继续获得奖金 所以新增判断当前阶段是否申诉成功过
      const delayTime = getHoursDiffFromNow(
        detailData?.order?.creatorNextOperateDate
      );
      setNowSuccessNode(detailData.order?.displayStatus);
      if (
        (delayTime > 0 || detailData.order?.isCurrentStageHasApprovedAppeal) &&
        detailData?.order?.interestedPriceFields?.[0]?.bonusAmount > 0
      ) {
        setShowModalBonusNext(true);
      } else {
        setShowModalNodeSuccess(true);
      }
    } catch (error) {
      toast.error(
        t('modal.address.submit.error', { defaultValue: 'Submission failed.' })
      );
    }
  };

  const handleAddressChange = (event: any) => {
    const { complete, value, rfcValue } = event;

    const { address, name, phone } = value;
    setAddress({
      ...address,
      name,
      phone,
      rfcValue: address?.country === 'MX' ? rfcValue : ''
    });
    setStripeComplete(complete);

    const addressText = `${(address as any)?.line1 || ''} ${(address as any)?.line2 || ''}`;
    const hasProhibitedPOBox = containsProhibitedPOBox(addressText);
    if (hasProhibitedPOBox) {
      // 每次检测到禁止内容都提示，避免从一种禁止写法改成另一种时用户看不到报错
      toast.error(
        t('modal.address.no_po_box.error', {
          defaultValue:
            'Please provide a physical shipping address (no PO Boxes).'
        })
      );
      setPoBoxInvalid(true);
      setComplete(false);
      return;
    }
    if (poBoxInvalid) {
      setPoBoxInvalid(false);
    }

    if (address?.country === 'MX' && !validateRFC(rfcValue)) {
      setComplete(false);
      return;
    }
    setComplete(complete);
  };

  return (
    <>
      {isMobile() ? (
        <BottomDrawer
          visible={isShowAddressModal}
          handleClose={() => setShowAddressModal(false)}
          handleSubmit={handleSubmit}
          isShowHeader={false}
          isShowFooter={stepNodes !== 1}
          isShowBack={stepNodes !== 0 ? true : false}
          handleBack={() => setStepNodes(1)}
          okText={t('modal.address.confirm', { defaultValue: 'Submit' })}
        >
          {stepNodes !== 0 && (
            <TopStep stepNodes={stepNodes} setStepNodes={setStepNodes} />
          )}
          {stepNodes === 1 && (
            <SelectProductContent
              autoFocus={isShowAddressModal && stepNodes === 1}
              stepNodes={stepNodes}
              setStepNodes={setStepNodes}
              skuListData={skuListData}
              setSkuListData={setSkuListData}
              setShowAddressModal={setShowAddressModal}
            />
          )}
          {(stepNodes === 0 || stepNodes === 2) && (
            <ModalContent
              autoFocus={
                isShowAddressModal && (stepNodes === 0 || stepNodes === 2)
              }
              handleAddressChange={handleAddressChange}
              addressInfo={address}
              complete={stripeComplete}
              creatorAgreementError={creatorAgreementError}
              setCreatorAgreementError={setCreatorAgreementError}
              addressElementRef={addressElementRef}
              showRfcValidationRef={showRfcValidationRef}
              addAddressToAccount={addAddressToAccount}
              setAddAddressToAccount={setAddAddressToAccount}
            />
          )}
        </BottomDrawer>
      ) : (
        <WebModal
          visible={isShowAddressModal}
          handleClose={() => setShowAddressModal(false)}
          handleSubmit={handleSubmit}
          isShowHeader={false}
          isShowFooter={stepNodes !== 1}
          okText={t('modal.address.confirm', { defaultValue: 'Submit' })}
          isShowBack={stepNodes !== 0 ? true : false}
          handleBack={() => setStepNodes(1)}
        >
          {stepNodes !== 0 && (
            <TopStep stepNodes={stepNodes} setStepNodes={setStepNodes} />
          )}
          {stepNodes === 1 && (
            <SelectProductContent
              autoFocus={isShowAddressModal && stepNodes === 1}
              stepNodes={stepNodes}
              setStepNodes={setStepNodes}
              skuListData={skuListData}
              setSkuListData={setSkuListData}
              setShowAddressModal={setShowAddressModal}
            />
          )}
          {(stepNodes === 0 || stepNodes === 2) && (
            <ModalContent
              autoFocus={
                isShowAddressModal && (stepNodes === 0 || stepNodes === 2)
              }
              handleAddressChange={handleAddressChange}
              addressInfo={address}
              complete={stripeComplete}
              setCreatorAgreementError={setCreatorAgreementError}
              creatorAgreementError={creatorAgreementError}
              addressElementRef={addressElementRef}
              showRfcValidationRef={showRfcValidationRef}
              addAddressToAccount={addAddressToAccount}
              setAddAddressToAccount={setAddAddressToAccount}
            />
          )}
        </WebModal>
      )}
    </>
  );
};

export default ModalAddress;
